import { Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { capitalizeFirstLetter } from 'BreetHelpers';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { IModernDropDown } from '../../Dropdown.helpers';
import { ModernDropDownControl } from '../controls/Modern';
import { ModernDropdownItems } from '../items/Modern';

const ModernDropDown = ({
	handleItemSelect,
	selectedItem,
	items,
	label,
	id,
	children,
	errorMessage,
	hideSearch = true,
	searchItemPlaceholder,
	CustomItemOption,
	disabled,
	wrapperClassName,
}: IModernDropDown) => {
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);

	const toggle = useCallback(() => setOpen((prev) => !prev), []);
	const close = useCallback(() => setOpen(false), []);

	useEffect(() => {
		if (!open) return undefined;

		const handleOutside = (e: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
		};

		document.addEventListener('mousedown', handleOutside);
		return () => document.removeEventListener('mousedown', handleOutside);
	}, [open]);

	return (
		<div
			className={classNameMerger('dropdown modern-dropdown', wrapperClassName)}
			data-testid='dropdown'
			ref={rootRef}
		>
			<div
				className={classNameMerger(
					'dropdowninput modern-dropdown_panel',
					!!disabled && 'disabled',
					open && 'open',
					'bg-primary-500'
				)}
			>
				<div className='dropdown_content'>
					<ModernDropDownControl
						selectedItem={selectedItem}
						buttonRef={buttonRef}
						handleVisibility={toggle}
						visible={open}
						isLabel={!!label}
						disabled={disabled}
					/>

					{!!label && (
						<label
							className='bt_label'
							htmlFor={id}
						>
							{label}
						</label>
					)}

					{children}
				</div>

				{open && (
					<ModernDropdownItems
						handleItemSelect={handleItemSelect}
						handleClose={close}
						hideSearch={hideSearch}
						selectedItem={selectedItem}
						items={items}
						searchItemPlaceholder={searchItemPlaceholder}
						CustomItemOption={CustomItemOption}
					/>
				)}
			</div>
			{!!errorMessage && (
				<Typography
					as='p'
					className='dropdown_errormessage'
				>
					{capitalizeFirstLetter(errorMessage)}
				</Typography>
			)}
		</div>
	);
};

export default ModernDropDown;
